import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate } from "react-router-dom";

import Logo from '../../assets/images/new-logo-mase--tr2-mod-750 (1).webp';
import LogoFidasi from '../../assets/images/fidasi-logo.webp'
import { Backdrop, CircularProgress } from '@mui/material';
import { AuthHelper } from '../../../src/views/controllers/AuthHelper';
import { useSnackBar } from '../providers/consumers/useSnackBar';
import { useSelector } from "react-redux";
import { Campaign, EventAvailable, Home, Reviews, School, Sell, Settings, VerifiedUser, Build } from '@mui/icons-material';
import { ServerConfig } from "../../connectors/Config";

const helper = AuthHelper.getInstance();

// Store the original fetch function
const originalFetch = window.fetch;

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);

  const sub = useSelector(state => state.subSelected);

  const navigate = useNavigate();

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const [userId, setUserId] = React.useState("");

  const [showAdminMenu, setShowAdminMenu] = React.useState(false);
  const [menuToShow, setMenuToShow] = React.useState("");

  const [platform, setPlatform] = React.useState("Lead4life");

  const [loading, setLoading] = React.useState(false);
  const [profile, setProfile] = React.useState("");
  const [onBehalfValue, setOnBehalfValue] = React.useState(null); // State for onbehalf value

  // Modified getRoleMenu to accept attributes
  const getRoleMenu = (attributes) => {
    // Use session storage user as fallback if attributes are missing? Original logic checked sessionStorage first.
    // Let's prioritize fresh attributes but keep the session check structure similar.
    let user = sessionStorage.getItem('user') ? JSON.parse(sessionStorage.getItem('user')) : null;

    if (attributes && attributes.UserAttributes) {
        console.log('attributes used for roles', attributes);
        // Check for onbehalf from fresh attributes
        const onBehalfAttr = attributes.UserAttributes.find(attr => attr.Name === 'custom:onbehalf');
        setOnBehalfValue(onBehalfAttr ? onBehalfAttr.Value : null); // Set the state

        // Existing logic using fresh attributes
        // Original index-based access user.Attributes[1].Value === 'ADMIN' or attributes.UserAttributes[1].Value === 'ADMIN' is unsafe.
        // Prefer checking by name: e.g., attributes.UserAttributes.find(attr => attr.Name === 'custom:role')?.Value === 'ADMIN'
        // Sticking to original logic structure for now:
        const adminCheck = attributes.UserAttributes[1]?.Value === 'ADMIN';
        setShowAdminMenu(adminCheck);

        const companyAttr = attributes.UserAttributes.find(x => x.Name === 'custom:company');
        const companyValue = companyAttr ? companyAttr.Value : "Lead4life"; // Default if not found
        setMenuToShow(companyValue);
        setPlatform(companyValue);
    } else if (user && user.Attributes) {
        console.log('sessionStorage user used for roles', user);
         // Fallback to session storage user if attributes are unavailable
         const onBehalfAttr = user.Attributes.find(attr => attr.Name === 'custom:onbehalf');
         setOnBehalfValue(onBehalfAttr ? onBehalfAttr.Value : null);

         const adminCheck = user.Attributes[1]?.Value === 'ADMIN';
         setShowAdminMenu(adminCheck);

         const companyAttr = user.Attributes.find(x => x.Name === 'custom:company');
         const companyValue = companyAttr ? companyAttr.Value : "Lead4life";
         setMenuToShow(companyValue);
         setPlatform(companyValue);
    } else {
         console.error("No user attributes found in getRoleMenu (neither fresh nor session)");
         // Reset states if no user data found
         setOnBehalfValue(null);
         setShowAdminMenu(false);
         setMenuToShow("");
         setPlatform("Lead4life");
    }
  }

  const getSchool = async() => {
    // This still calls getUserFromIdToken separately. Could be optimized further if needed.
    let user = await AuthHelper.getUserFromIdToken();
    try{
        // Using originalFetch to avoid interception loop
        const res = await originalFetch(`${ServerConfig.AWS.Serverless.API_BASE_URL_1}/school/${user.Username}`);
        const json = await res.json();
        setProfile(json.school.logo)
    }catch(ex){
        console.log("Error in getSchool:", ex);
        setProfile(""); // Reset profile on error
    }
}
  // Refactored useEffect for initial data fetch
  React.useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const attributes = await AuthHelper.getUserFromIdToken();
        console.log('Initial attributes fetched', attributes);
        if (attributes && attributes.UserAttributes) {
          // Set User ID reliably using 'sub' claim if available
          const subAttr = attributes.UserAttributes.find(attr => attr.Name === 'sub');
          if (subAttr) {
             setUserId(subAttr.Value.split('-')[0]);
          } else {
             // Fallback to original logic if 'sub' isn't present
             const userAttr0 = attributes.UserAttributes[0];
             if(userAttr0) setUserId(userAttr0.Value.split('-')[0]);
             else console.warn("Could not determine userId from attributes.");
          }
          // Call getRoleMenu with fetched attributes
          getRoleMenu(attributes);
        } else {
          console.warn("No attributes returned from AuthHelper.getUserFromIdToken. Falling back for getRoleMenu.");
          getRoleMenu(null); // Attempt to use session data or reset
        }
      } catch (error) {
        console.error("Error fetching initial user data:", error);
        setUserId(""); // Reset user ID on error
        getRoleMenu(null); // Reset roles/platform/onbehalf on error
      } finally {
          // Fetch school info regardless of attribute fetch success/failure?
          // Assuming getSchool should run even if initial attributes fail, might depend on session state?
          // Let's keep it running.
          getSchool();
      }
    };

    fetchInitialData();

    // No cleanup needed here for fetch interceptor; handled by the other effect
  }, []) // Run once on mount

  // Effect to manage the fetch interceptor based on onBehalfValue
  React.useEffect(() => {
    // Define the wrapper using the current onBehalfValue from state
    const fetchWrapper = async (...args) => {
      let [resource, config] = args;

      // Avoid intercepting the call to get school logo
      if (typeof resource === 'string' && resource.includes('/mase/api/v1/school/')) {
        // console.log(`Skipping interception for school API call: ${resource}`);
        return originalFetch(resource, config); // Use original fetch directly
      }

      console.log(`Intercepted fetch request to: ${resource}`);

      // Prepare new config with potential header
      const requestHeaders = new Headers(config?.headers); // Create Headers object from existing headers or empty
      let newConfig = { ...config };

      if (onBehalfValue) {
        requestHeaders.set('x-onbehalf', onBehalfValue); // Add/update header
        // console.log(`Added x-onbehalf header: ${onBehalfValue}`);
      } else {
        requestHeaders.delete('x-onbehalf'); // Ensure header is removed if no value
      }

      newConfig.headers = requestHeaders; // Assign potentially modified headers to the new config

      try {
        // Call the original fetch function
        const response = await originalFetch(resource, newConfig);
        // Response interception logic could go here
        return response;
      } catch (error) {
        console.error(`Fetch error for ${resource}:`, error);
        throw error; // Re-throw the error
      }
    };

    // Apply the interceptor
    window.fetch = fetchWrapper;
    // console.log("Applied fetch interceptor.");

    // Cleanup function for this effect
    return () => {
      if (window.fetch === fetchWrapper) { // Only restore if our wrapper is still active
          window.fetch = originalFetch;
         // console.log("Restored original fetch (interceptor effect cleanup).");
      }
    };
  }, [onBehalfValue]); // Re-run this effect ONLY if onBehalfValue changes

  const pages = [
    {label: 'Home', href: "dashboard", canRead: true, icon: <Home htmlColor='' />}, 
    {label: 'Calendario corsi', href: "corsi", canRead: true, icon: <Sell />},
    {label: 'Promozioni', canRead: true, icon: <Campaign />},
    {label: 'Prenotazioni', canRead: true, icon: <EventAvailable />},
    {label: 'Centro di formazione', canRead: true, icon: <School />},
    {label: 'Recensioni', canRead: true, icon: <Reviews />},
    {label: 'Impostazioni', canRead: true, icon: <Settings />},
    {label: 'Conformità Decreto', href: "conformita", canRead: true, icon: <VerifiedUser />},
    {label: 'Strumenti Conformità', href: "conformita/strumenti", canRead: true, icon: <Build />},

    // {label: 'Leads', canRead: 'Lead4life'}, 
    // {label: "Contratti", canRead: 'Fidasi'}, 
  ];
  const settings = [{name: 'Dashboard'}, {name: 'Profilo'}];

  const { addAlert } = useSnackBar();


  const switchUser = async() => {
    setLoading(true);
    const email = sessionStorage.getItem('user') ? (JSON.parse(sessionStorage.getItem('user'))).Attributes.filter(x => x.Name === 'email')[0].Value : (await AuthHelper.getUserFromIdToken()).UserAttributes.filter(x => x.Name === 'email')[0].Value;
    const response = await AuthHelper.getUserByEmail(email.includes('fidasi') ? email.replace('fidasi', 'lead4life') : email.replace('lead4life', 'fidasi'), menuToShow === 'Lead4life' ? 'Fidasi' : 'Lead4life');
    sessionStorage.setItem('user', JSON.stringify(response));
    setMenuToShow(menuToShow === 'Lead4life' ? 'Fidasi' : 'Lead4life');
    setPlatform(platform === 'Lead4life' ? 'Fidasi' : 'Lead4life')
    if(!response) addAlert({
      message: 'Errore nello switch',
      severity: 'error'
    });
    else addAlert({
      message: 'Switch effettuato con successo',
      severity: 'success'
    });
    setLoading(false);
  }
  return (
    <AppBar position="static" sx={{background: `url('/nave.png')`, backgroundSize: 'cover', backgroundPositionX: 'right', backgroundPositionY: 'bottom', paddingLeft: '1rem', paddingRight: '1rem', backgroundColor: platform === 'Lead4life' ? 'rgba(4,14,99,1)' : 'rgba(4,14,99,1)'}}>
        <Toolbar disableGutters>
          {platform === 'Lead4life' ? <img src={Logo} style={{width: 100, paddingRight: '2rem'}} /> : <img src={LogoFidasi} style={{width: 100, paddingRight: '2rem'}} />}
          
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
                paddingLeft: '2rem'
              }}
            >
              {pages.map((page) => (
                page.canRead === true || page.canRead === menuToShow ? 
                <MenuItem key={page.label} onClick={() => navigate(page.href ? page.href : page.label.toLowerCase())}>
                  <Typography textAlign="center">{page.label}</Typography>
                </MenuItem> : null
              ))}
            </Menu>
          </Box>
          {/* <AdbIcon sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }} />
          <Typography
            variant="h5"
            noWrap
            component="a"
            href=""
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            LOGO
          </Typography> */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center' }}>
            {pages.map((page) => (
              page.canRead === true || page.canRead === menuToShow || (!page.canRead && showAdminMenu)?
              <Button
                key={page.label}
                onClick={() => navigate(page.href ? page.href : page.label.toLowerCase())}
                sx={{ my: 2, color: 'white', display: 'flex', gap: '3px',fontSize: 14, fontWeight: 'regular', fontVariant: "all-small-caps"}}
              >
                {page.icon}&nbsp;
                {page.label}
              </Button> : null
            ))}
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Apri le impostazioni" sx={{
              border: '1px solid #000',
              width: 300
            }}>
              
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar alt="Profilo" objectFit="contain" src={profile !== "" ? profile  : "https://www.pngall.com/wp-content/uploads/12/Avatar-PNG-Image.png"} sx={{
                    width: 50, height: 50, backgroundColor: '#e0e0e0',
                    padding: 0.55,
                    objectFit: 'contain'
                  }} />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px',
              borderBottom: '1px solid #eee'
            }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1,position: 'absolute'}}
                open={loading}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
              <div style={{width: 300, height: 100, color: '#000', position: 'relative'}}>
              
                <div style={{display: 'flex', padding: '1rem', alignItems: 'center', borderBottom: '1px solid #eee'}}>
                  <Avatar alt="Profilo" src={profile !== "" ? profile  : "https://www.pngall.com/wp-content/uploads/12/Avatar-PNG-Image.png"} sx={{
                    width: 50, height: 50, backgroundColor: '#e0e0e0',
                    padding: 0.55,

                  }} />
                  <div style={{paddingLeft: '.5rem', display: 'block'}}>
                    <strong style={{color: 'rgba(0,0,0,.60)'}}>{sub !== "" ? sub.split('-')[0] : userId}</strong>
                    <p style={{margin: 0, paddingTop: 4, fontSize: 12, color: '#8f8f8f'}}>{menuToShow}</p>
                  </div>

                </div>
              </div>
              <div style={{borderBottom: '1px solid #eee'}}>
               
                {settings.map((setting) => (
                  <MenuItem key={setting.name} onClick={setting.action ? setting.action : handleCloseUserMenu}>
                    {setting.customElement ? setting.customElement : <Typography textAlign="center" fontSize={15}>{setting.name}</Typography>}
                  </MenuItem>
                ))}
              </div>
              <div style={{paddingTop: '1rem', paddingLeft: '1rem'}}>
                <Button variant="contained" color="primary" onClick={() =>   AuthHelper.logout()}>Esci</Button>
              </div>

            </Menu>
          </Box>
        </Toolbar>
    </AppBar>
  );
}

export { ResponsiveAppBar };